/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-get, ember/no-mixins, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { service } from '@ember/service';
import EmberObject, { get, action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import { getCurrentMilestone } from '@linc-technologies/linced-common/utils/progressions';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import ProgressionJudgement from '@linc-technologies/linced-common/enums/progression-judgement';
import ProgressionType from '@linc-technologies/linced-common/enums/progression-type';
import SearchMode from '@linc-technologies/linced-common/enums/person-option-search-mode';
import { getProgressionJudgementLabel } from '@linc-technologies/linced-common/helpers/progression-judgement-label';
import FlipMixer from 'linced-app/mixins/flip-mixer';
import {
  byKeys,
  compact,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';

function sortByJudgementAndName(a, b) {
  // Sort by judgement (descending)
  if (a.judgement < b.judgement) {
    return 1;
  } else if (a.judgement > b.judgement) {
    return -1;
  }

  // If judgement is equal, sort by name (ascending)
  if (a.name < b.name) {
    return -1;
  } else if (a.name > b.name) {
    return 1;
  }

  return 0;
}

export default class GroupLearners extends Component.extend(FlipMixer) {
  @action
  setFirstNameFilter(value) {
    this.firstNameFilter = value;
  }

  @action
  setGenderFilter(value) {
    this.genderFilter = value;
  }

  @action
  setHomeroomFilter(value) {
    this.homeroomFilter = value;
  }

  @action
  setLastNameFilter(value) {
    this.lastNameFilter = value;
  }

  @action
  setYearLevelFilter(value) {
    this.yearLevelFilter = value;
  }

  // Services
  @service
  dates;

  @service
  features;

  @service
  fieldOptions;

  @service
  permissions;

  @service
  personOptions;

  @service
  roles;

  @service
  session;

  @service
  store;

  @service
  strings;

  ProgressionJudgement = ProgressionJudgement;
  SearchMode = SearchMode;
  childSelector = '.learner-list > .badge-card';
  @tracked sortBy = 'firstName';
  @tracked sortType = 'field';
  @tracked sortDir = 'asc';
  @tracked
  progressCache = [];
  subjects = [];
  @tracked subjectId = '';

  @computed('subjectId', 'subjects.@each.{id,label}')
  get subjectName() {
    if (!isEmpty(this.subjectId)) {
      let subject = findBy(this.subjects, 'id', this.subjectId);
      if (!isEmpty(subject)) {
        return subject.label;
      }
    }
    return '';
  }

  // Field filters.
  @tracked lastNameFilter = '';

  @tracked firstNameFilter = '';
  @tracked genderFilter = '';
  @tracked yearLevelFilter = '';
  @tracked homeroomFilter = '';

  @computed(
    'lastNameFilter',
    'firstNameFilter',
    'genderFilter',
    'yearLevelFilter',
    'homeroomFilter',
  )
  get fieldFilters() {
    return ['lastName', 'firstName', 'gender', 'yearLevel', 'homeroom'].reduce(
      (filters, key) => {
        let value = this.get(`${key}Filter`);
        if (!isEmpty(value)) {
          filters.push({ key, value });
        }
        return filters;
      },
      [],
    );
  }

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed(
    'mode',
    'sortBy',
    'sortDir',
    'sortType',
    'learners.@each.id',
    'fieldFilters.@each.{key,value}',
    'yearLevelOptions.[]',
    'progressCache.@each.{subjectId,loading,data}',
  )
  get sortedFilteredLearners() {
    // Filter the group's learners, if applicable.
    let filteredLearners = this.learners;
    if (this.mode === SearchMode.LIST && !isEmpty(this.fieldFilters)) {
      let yearLevelFilterValue = getNumericYearLevelFilter(
        findBy(this.fieldFilters, 'key', 'yearLevel'),
        this.yearLevelOptions,
      );

      filteredLearners = this.learners.filter((learner) => {
        let person = learner.get('person');
        return this.fieldFilters.every((filter) => {
          let key = filter.key;
          switch (filter.key) {
            case 'yearLevel':
              return filterValue(
                person.get('numericCurrentYearLevel'),
                DataType.INTEGER,
                yearLevelFilterValue,
              );

            case 'homeroom':
              key = 'homeroom.name';
              break;
          }
          return filterValue(person.get(key), DataType.STRING, filter.value);
        });
      });
    }

    // Sort the (filtered) list of learners.
    let sortedLearners = filteredLearners;
    let invert = false;
    let progress;
    switch (this.sortType) {
      case 'field':
        if (this.sortBy === 'firstName') {
          sortedLearners = filteredLearners.sort(
            byKeys('person.firstName', 'person.lastName'),
          );
        } else if (this.sortBy === 'lastName') {
          sortedLearners = filteredLearners.sort(
            byKeys('person.lastName', 'person.firstName'),
          );
        } else {
          sortedLearners = filteredLearners.sort(
            byKeys(`person.${this.sortBy}`),
          );
        }
        if (this.sortDir === 'desc') {
          invert = true;
        }
        break;
      case 'progress':
        progress = findBy(this.progressCache, 'subjectId', this.subjectId);
        if (!isEmpty(progress) && !progress.get('loading')) {
          sortedLearners = compact(
            progress.get('data').map((record) => {
              return findBy(filteredLearners, 'person.id', record.personId);
            }),
          );
          // The above returns learners sorted by judgement descending, so it's
          // backwards compared to a field-based sort.
          if (this.sortDir === 'asc') {
            invert = true;
          }
        }
        break;
    }

    // Return either as-is or reversed, depending on the requested direction.
    if (invert) {
      return sortedLearners.reverse();
    }
    return sortedLearners;
  }

  @computed(
    'learners.@each.id',
    'subjectId',
    'progressCache.@each.{subjectId,loading}',
  )
  get progressByLearner() {
    if (isEmpty(this.subjectId)) {
      // No subject selected.  Just return an empty map.
      return {};
    }
    let cache = findBy(this.progressCache, 'subjectId', this.subjectId);
    if (isEmpty(cache)) {
      // No progressions for this subject.
      this.primeProgressCache(this.subjectId);
      return {};
    }

    return Object.fromEntries(
      compact(
        this.learners.map((learner) => {
          let data = findBy(cache.data, 'personId', learner.get('person.id'));
          if (isEmpty(data)) {
            return;
          }

          return [learner.get('person.id'), data.judgement];
        }),
      ),
    );
  }

  @computed('sortedFilteredLearners.[]', 'progressByLearner', 'subjectId')
  get exportData() {
    return this.sortedFilteredLearners.map((learner) => {
      let judgement = '';
      if (!isEmpty(this.subjectId) && !isEmpty(this.progressByLearner)) {
        judgement = get(this.progressByLearner, learner.get('person.id'));
        judgement = getProgressionJudgementLabel(
          judgement,
          { capitalize: true },
          this.strings,
        );
      }
      return [
        learner.get('person.firstName'),
        learner.get('person.lastName'),
        learner.get('person.preferredGender'),
        learner.get('person.currentYearLevel'),
        learner.get('person.homeroom.name') || '',
        judgement,
      ];
    });
  }

  showEthnicFlag = false;
  showLearnerFlags = false;

  // allowProfileUploads determines whether drag-and-drop profile image uploads
  // are enabled for learner badge cards.
  allowProfileUploads = false;

  // isGroupHomepage determines whether this is the full group homepage with
  // features such as view modes and profile image uploads enabled.
  @tracked isGroupHomepage = false;

  // tableWrapComponent determines whether to wrap the table (in List View) in
  // a table-tools component or a dummy blank component.
  get tableWrapComponent() {
    if (this.isGroupHomepage) {
      return 'table-tools';
    }
    return 'blank-component';
  }

  @tracked mode = SearchMode.GRID;
  @tracked
  defaultMode = SearchMode.GRID;

  // personCardOpen determines whether the person nav card is open.
  @tracked
  personCardOpen = false;

  // selectedPerson is the person whose card is being displayed, if applicable.
  @tracked
  selectedPerson = null;

  init() {
    super.init(...arguments);
    this.progressCache = [];
    this.initMode();
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    this.initMode();
  }

  initMode() {
    if (this.isGroupHomepage) {
      let defaultMode = this.get('personOptions.groupMode');
      this.mode = defaultMode;
      this.defaultMode = defaultMode;
    }
  }

  primeProgressCache(subjectId) {
    let progress = findBy(this.progressCache, 'subjectId', subjectId);
    if (isEmpty(progress)) {
      this.progressCache = [
        ...this.progressCache,
        EmberObject.create({
          subjectId,
          loading: true,
          data: [],
        }),
      ];

      let query = {
        groupId: this.group.id,
        latest: true,
        type: ProgressionType.PROGRESSION,
        goalSubjectId: subjectId,
      };

      // Get the milestones which apply to this subject.
      let milestones = [];
      let subject = this.store.peekRecord('goals/subject', subjectId);
      if (!isEmpty(subject) && !isEmpty(subject.milestones)) {
        milestones = subject.milestones;
      }

      return this.store
        .query('milestones/progression', query)
        .then((results) => {
          let cache = findBy(this.progressCache, 'subjectId', subjectId);
          let data = this.learners
            .map((learner) => {
              let milestone = getCurrentMilestone(
                milestones,
                learner.person,
                this.dates.rolloverMonthDate,
              );
              let personId = learner.get('person.id');
              let judgement = 0; // Must be 0 if no progression is found, so that it sorts correctly.
              let progressions = results.filter(
                (progression) => progression.person_id === personId,
              );
              if (!isEmpty(milestone)) {
                let record = progressions.find(
                  (progression) => progression.milestone_id === milestone.id,
                );
                if (isEmpty(record)) {
                  // No progression for the current milestone.  Use the progression with the highest milestone index.
                  let sorted = progressions
                    .slice()
                    .sort(
                      (a, b) =>
                        a.get('milestone.sequence') -
                        b.get('milestone.sequence'),
                    );
                  record = sorted[sorted.length - 1];
                }
                if (!isEmpty(record)) {
                  judgement = record.judgement || 0;
                }
              }
              return {
                personId,
                judgement,
                name: learner.get('person.fullName'),
                badgeClass: `colours-progress--${judgement}`,
              };
            })
            .sort(sortByJudgementAndName);

          cache.setProperties({
            loading: false,
            data,
          });
        });
    } else {
      return Promise.resolve();
    }
  }

  @action
  setMode(mode) {
    this.mode = mode;
  }

  @action
  setDefaultMode() {
    this.personOptions.setGroupMode(this.mode).then(() => {
      // Once resolved, update our local default to the new value.
      // This will still be the old value if there was an error, so the
      // button to save as default will still be present.
      this.defaultMode = this.get('personOptions.groupMode');
    });
  }

  @action
  setListSort({ sortProp, sortDir }) {
    let sortType = 'field';
    if (sortProp.startsWith('subject:')) {
      sortType = 'progress';
      if (this.sortBy !== sortProp) {
        // Initial sort should be descending, to match Grid View.
        sortDir = 'desc';
      }
      this.primeProgressCache(this.subjectId).then(() => {
        this.sortBy = sortProp;
        this.sortDir = sortDir;
        this.sortType = sortType;
      });
    } else {
      this.sortBy = sortProp;
      this.sortDir = sortDir;
      this.sortType = sortType;
    }
  }

  @action
  setSubject(subject) {
    if (isEmpty(subject)) {
      // Allows for the selection to be cleared.
      this.subjectId = null;
      if (this.sortType === 'progress') {
        // Reset sort props to defaults.
        this.sortBy = 'firstName';
        this.sortDir = 'asc';
        this.sortType = 'field';
      }
    } else {
      this.subjectId = subject.id;
      this.primeProgressCache(subject.id);
      if (this.sortType === 'progress') {
        this.sortBy = `subject:${subject.id}`;
      }
    }
  }

  @action
  openPersonCard(person) {
    this.personCardOpen = true;
    this.selectedPerson = person;
  }

  @action
  sortByField(fieldPath) {
    // Sorting by field defaults to ascending.
    let sortDir = 'asc';
    if (this.sortBy === fieldPath) {
      // Same field - invert direction.
      sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    }
    this.sortBy = fieldPath;
    this.sortType = 'field';
    this.sortDir = sortDir;
  }

  @action
  sortBySubject(subjectId) {
    // Sorting by subject/progress defaults to descending.
    let sortDir = 'desc';
    if (this.subjectId === subjectId) {
      // Same field - invert direction.
      sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    }
    this.primeProgressCache(subjectId).then(() => {
      this.subjectId = subjectId;
      this.sortBy = `subject:${subjectId}`;
      this.sortType = 'progress';
      this.sortDir = sortDir;
    });
  }

  @action
  getBadgeClass(personId) {
    if (this.sortType === 'progress') {
      let cache = findBy(this.progressCache, 'subjectId', this.subjectId);
      if (isEmpty(cache)) {
        return '';
      }
      let data = findBy(cache.data, 'personId', personId);
      if (!isEmpty(data)) {
        return data.badgeClass;
      }
    }

    return '';
  }
}
